import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  MAIL_MESSAGE_IDS_QUERY_KEY,
  MAIL_MESSAGES_QUERY_KEY,
  USER_UNSEEN_EVENTS_QUERY_KEY,
} from '@/api/queryKeys';
import { useFollowUpStatus } from '@/pages/Mail/hooks/useFollowUpStatus';
import { useMailMessageIds } from '@/pages/Mail/hooks/useMailMessageIds';
import {
  mailMessagesPageQueryKey,
  useMailMessagePages,
} from '@/pages/Mail/hooks/useMailMessagePages';
import { useMarkMailRead } from '@/pages/Mail/hooks/useMarkMailRead';
import {
  FollowUpStatus,
  MailFolderType,
  MailSearchType,
  MessageStatus,
} from '@/types/components';
import type { MailMessage } from '@/types/mail';

type LocalMailChanges = Record<string, Partial<MailMessage>>;

type LoadedPageCounts = Record<string, number>;

const nextFollowUpStatus: Record<FollowUpStatus, FollowUpStatus> = {
  [FollowUpStatus.None]: FollowUpStatus.Marked,
  [FollowUpStatus.Marked]: FollowUpStatus.Completed,
  [FollowUpStatus.Completed]: FollowUpStatus.None,
};

const hasLeftFolder = (
  mail: MailMessage,
  folderType: MailFolderType | null,
): boolean => {
  switch (folderType) {
    case MailFolderType.Unread:
      return mail.status !== MessageStatus.Unread;
    case MailFolderType.FollowUp:
      return mail.followUpStatus !== FollowUpStatus.Marked;
    default:
      return false;
  }
};

interface Params {
  folderType: MailFolderType | null;
  searchType: MailSearchType | null;
  searchValue: string;
  onFollowUpError: () => void;
}

export const useMailMessages = ({
  folderType,
  searchType,
  searchValue,
  onFollowUpError,
}: Params) => {
  const [selectedMailId, setSelectedMailId] = useState<string | null>(null);
  const [loadedPageCounts, setLoadedPageCounts] = useState<LoadedPageCounts>(
    {},
  );
  const [localChanges, setLocalChanges] = useState<LocalMailChanges>({});
  const scrollOffsetRef = useRef<number>(0);

  const queryClient = useQueryClient();

  const { idPages, isFetching: isFetchingIds } = useMailMessageIds({
    folderType,
    searchType,
    searchValue,
  });

  const pageCountKey = `${folderType}:${searchValue}`;
  const isPageCached = (pageIds: number[]) =>
    queryClient.getQueryData(mailMessagesPageQueryKey(pageIds)) !== undefined;
  const cachedPageCount = idPages.filter(isPageCached).length;
  const requestedPageCount = loadedPageCounts[pageCountKey] ?? 1;
  const loadedPageCount = Math.max(requestedPageCount, cachedPageCount);

  const {
    mails: loadedMails,
    isFetchingFirstPage,
    isFetchingNextPage,
  } = useMailMessagePages(idPages, loadedPageCount);

  const applyLocalChanges = (mail: MailMessage): MailMessage => ({
    ...mail,
    ...localChanges[mail.id],
  });

  const isHiddenFromFolder = (mail: MailMessage) =>
    mail.id !== selectedMailId && hasLeftFolder(mail, folderType);

  const changedMails = loadedMails.map(applyLocalChanges);
  const mails = changedMails.filter((mail) => !isHiddenFromFolder(mail));
  const hiddenCount = changedMails.length - mails.length;
  const selectedMail = mails.find(({ id }) => id === selectedMailId) ?? null;
  const hasMorePages = loadedPageCount < idPages.length;
  const totalCount = idPages.flat().length - hiddenCount;
  const isLoading = (isFetchingIds || isFetchingFirstPage) && !mails.length;

  const findMail = (id: string) => mails.find((mail) => mail.id === id);

  const changeMail = (id: string, change: Partial<MailMessage>) => {
    setLocalChanges((prev) => ({ ...prev, [id]: { ...prev[id], ...change } }));
  };

  const { markRead } = useMarkMailRead();

  const { mutate: changeFollowUpStatus } = useFollowUpStatus({
    onError: ({ messageId, previousStatus }) => {
      changeMail(messageId, { followUpStatus: previousStatus });
      onFollowUpError();
    },
  });

  const cycleFollowUp = (id: string) => {
    const mail = findMail(id);

    if (!mail) {
      return;
    }

    const followUpStatus = nextFollowUpStatus[mail.followUpStatus];

    changeMail(id, { followUpStatus });
    changeFollowUpStatus({
      messageId: id,
      followUpStatus,
      previousStatus: mail.followUpStatus,
    });
  };

  const openMail = (id: string) => {
    const isUnread = findMail(id)?.status === MessageStatus.Unread;

    setSelectedMailId(id);

    if (isUnread) {
      changeMail(id, { status: MessageStatus.Read });
      markRead(id);
    }
  };

  const closeMail = () => {
    setSelectedMailId(null);
  };

  const refresh = () => {
    setLocalChanges({});
    setLoadedPageCounts({});
    scrollOffsetRef.current = 0;
    queryClient.resetQueries({ queryKey: MAIL_MESSAGES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: MAIL_MESSAGE_IDS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: USER_UNSEEN_EVENTS_QUERY_KEY });
  };

  const loadMorePages = () => {
    if (!folderType) {
      return;
    }

    setLoadedPageCounts((counts) => ({
      ...counts,
      [pageCountKey]: loadedPageCount + 1,
    }));
  };

  useEffect(() => {
    setSelectedMailId(null);
  }, [folderType]);

  useEffect(() => {
    scrollOffsetRef.current = 0;
  }, [folderType, searchValue]);

  return {
    mails,
    isLoading,
    totalCount,
    hasMorePages,
    isFetchingNextPage,
    loadMorePages,
    selectedMail,
    selectedMailId,
    scrollOffsetRef,
    applyLocalChanges,
    findMail,
    cycleFollowUp,
    openMail,
    closeMail,
    refresh,
  };
};
